/**
 * The event vocabulary. Pure — these functions build payloads and nothing else.
 *
 * Naming lives in the core rather than at each call site so the Worker and the browser emit
 * the same strings by construction. A rename that only half-lands is the failure this
 * prevents, and it is invisible until a funnel quietly reads zero.
 *
 * Every event here goes to PostHog — see CLAUDE.md, User Events. Statsig was removed
 * on 2026-09-12; it received a duplicate copy of all of this.
 */

import type { LabsTool } from './standards-default';

export type EventName =
  | 'objectives_page_viewed'
  | 'objectives_tools_list_opened'
  | 'objectives_access_denied'
  | 'objectives_profile_saved'
  | 'objectives_upload_started'
  | 'objectives_upload_rejected'
  | 'objectives_upload_succeeded'
  | 'objectives_analysis_started'
  | 'objectives_analysis_completed'
  | 'objectives_analysis_failed'
  | 'objectives_report_viewed'
  | 'objectives_document_stored'
  | 'objectives_document_refused'
  | 'objectives_document_deleted'
  | 'objectives_quota_exceeded'
  | 'objectives_view_toggled'
  | `preview_objectives_${PreviewAction}`;

export interface AnalyticsEvent {
  name: EventName;
  properties: Record<string, string | number | boolean | string[]>;
}

/** The report variant every browser-side funnel event carries, so the experiment is measurable. */
export type Variant = string;

export const objectivesPageViewed = (variant: Variant): AnalyticsEvent => ({
  name: 'objectives_page_viewed',
  properties: { experiment_variant: variant },
});

export const objectivesToolsListOpened = (): AnalyticsEvent => ({
  name: 'objectives_tools_list_opened',
  properties: {},
});

/**
 * Server-side and browser-side both. `reason` is a category — `disabled` since the access
 * gate became a kill switch — and never the address itself; a denial event is not a place to put an
 * identifier we have just decided we do not trust.
 */
export const objectivesAccessDenied = (reason: string): AnalyticsEvent => ({
  name: 'objectives_access_denied',
  properties: { reason },
});

export const objectivesProfileSaved = (
  jurisdiction: string,
  academicSubject: string,
  gradeCount: number,
  variant: Variant,
): AnalyticsEvent => ({
  name: 'objectives_profile_saved',
  properties: { jurisdiction, academic_subject: academicSubject, grade_count: gradeCount, experiment_variant: variant },
});

export const objectivesUploadStarted = (kind: string, variant: Variant): AnalyticsEvent => ({
  name: 'objectives_upload_started',
  properties: { kind, experiment_variant: variant },
});

export const objectivesUploadRejected = (reason: string): AnalyticsEvent => ({
  name: 'objectives_upload_rejected',
  properties: { reason },
});

export const objectivesUploadSucceeded = (
  kind: string,
  byteLength: number,
  variant: Variant,
): AnalyticsEvent => ({
  name: 'objectives_upload_succeeded',
  properties: { kind, byte_length: byteLength, experiment_variant: variant },
});

/**
 * Server-side only, and deliberately *not* `objectives_upload_succeeded`.
 *
 * The browser already emits the funnel event for a successful upload. If the Worker emitted
 * the same name, every upload would be counted twice and the funnel would silently read
 * double — the kind of error that is invisible until someone tries to compute a rate. Same
 * reasoning as `notify_signup_recorded` in packages/landing: the server's view of an event
 * is its own event.
 */
export const objectivesDocumentStored = (kind: string, byteLength: number): AnalyticsEvent => ({
  name: 'objectives_document_stored',
  properties: { kind, byte_length: byteLength },
});

/** Server-side only, for the same reason as `objectivesDocumentStored`. */
export const objectivesDocumentRefused = (reason: string): AnalyticsEvent => ({
  name: 'objectives_document_refused',
  properties: { reason },
});

/** Server-side only. The teacher removed a plan and everything read from it. */
export const objectivesDocumentDeleted = (): AnalyticsEvent => ({
  name: 'objectives_document_deleted',
  properties: {},
});

/**
 * Server-side only. `kind` is which ceiling was hit — uploads or analyses — never the count
 * or the account: the event exists to show whether the limits are ever felt by real
 * teachers, which is what decides whether they are set right.
 */
export const objectivesQuotaExceeded = (kind: string): AnalyticsEvent => ({
  name: 'objectives_quota_exceeded',
  properties: { kind },
});

/** Server-side only. */
export const objectivesAnalysisStarted = (): AnalyticsEvent => ({
  name: 'objectives_analysis_started',
  properties: {},
});

/**
 * Server-side only. The two provider labels are what tell a funnel reading zero matches
 * apart from a funnel reading fixtures — without them the numbers are uninterpretable.
 */
export const objectivesAnalysisCompleted = (facts: {
  segmentCount: number;
  findingCount: number;
  objectivesProvider: string;
  standardsProvider: string;
  truncated: boolean;
}): AnalyticsEvent => ({
  name: 'objectives_analysis_completed',
  properties: {
    segment_count: facts.segmentCount,
    finding_count: facts.findingCount,
    objectives_provider: facts.objectivesProvider,
    standards_provider: facts.standardsProvider,
    truncated: facts.truncated,
  },
});

export const objectivesAnalysisFailed = (reason: string): AnalyticsEvent => ({
  name: 'objectives_analysis_failed',
  properties: { reason },
});

/** The teacher flipped between reading their plan and reading the state's checklist. */
export const objectivesViewToggled = (view: 'plan' | 'standards'): AnalyticsEvent => ({
  name: 'objectives_view_toggled',
  properties: { view },
});

export const objectivesReportViewed = (
  findingCount: number,
  variant: Variant,
): AnalyticsEvent => ({
  name: 'objectives_report_viewed',
  properties: { finding_count: findingCount, experiment_variant: variant },
});

/** Explicit interactions; arrays represent the complete selection AFTER the action. */
export type PreviewAction =
  | 'states_selected' | 'subjects_selected' | 'grades_selected'
  | 'profile_save_clicked' | 'profile_change_clicked' | 'file_selected'
  | 'run_clicked' | 'run_completed' | 'run_failed' | 'read_another_clicked'
  | 'delete_clicked' | 'delete_cancelled' | 'delete_confirmed' | 'delete_failed'
  | 'filter_selected' | 'legend_toggled' | 'source_link_clicked'
  | 'welcome_viewed' | 'example_opened' | 'example_closed' | 'example_view_changed' | 'own_plan_clicked'
  // Server-side only: the shared standards default, below.
  | 'standards_default_saved' | 'standards_default_read';

export const previewObjectivesAction = (
  action: PreviewAction,
  properties: AnalyticsEvent['properties'] = {},
): AnalyticsEvent => ({ name: `preview_objectives_${action}`, properties });

/**
 * Server-side only. One write to the teacher's shared standards default, from any of the
 * four Labs tools — Objectives' own profile save included. `fields` names what the write
 * replaced; the values never leave. `matched_prefill` is absent, not false, when the caller
 * did not say — "unknown" must not count as "changed the prefill".
 *
 * Deliberately not `objectives_profile_saved`: that one is the browser's funnel step and
 * the tracker reads it, so the server never emits it.
 */
export const standardsDefaultSaved = (facts: {
  tool: LabsTool;
  fields: string[];
  matchedPrefill: boolean | null;
  gradesCollapsed: boolean;
}): AnalyticsEvent => previewObjectivesAction('standards_default_saved', {
  tool: facts.tool,
  fields: [...facts.fields],
  ...(facts.matchedPrefill === null ? {} : { matched_prefill: facts.matchedPrefill }),
  grades_collapsed: facts.gradesCollapsed,
  event_source: 'server',
});

/**
 * Server-side only. A Labs tool asked for the default to prefill a form.
 * `default_source_tool` is absent when there is no default, or the row predates the tool
 * stamp.
 */
export const standardsDefaultRead = (facts: {
  tool: LabsTool;
  hasDefault: boolean;
  defaultSourceTool: string | null;
}): AnalyticsEvent => previewObjectivesAction('standards_default_read', {
  tool: facts.tool,
  has_default: facts.hasDefault,
  ...(facts.defaultSourceTool === null ? {} : { default_source_tool: facts.defaultSourceTool }),
  event_source: 'server',
});

/** PostHog uses the product prefix; the bare name is what the record and tests use. */
export const posthogEventName = (name: EventName): string =>
  name.startsWith('preview_') ? name : `preview_${name}`;
